import type { DirectoryInvitationStatus, DirectorySubmissionInput } from "./types";
import { isDirectoryCategoryId } from "./directory-categories";
import { isValidDirectoryKeywords, sanitizeDirectoryKeywords } from "./directory-keywords";

/** Bucket privado donde se guardan los archivos de solicitudes pendientes. */
export const DIRECTORY_SUBMISSION_BUCKET = "directory-submissions";
/** Bucket publico existente (reutilizado para el contenido ya aprobado). */
export const DIRECTORY_MEDIA_BUCKET = "media";

export const DIRECTORY_ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"] as const;

export const DIRECTORY_MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const DIRECTORY_MAX_GALLERY = 8;

export type InvitationExpiryOption = "24h" | "7d" | "custom";

export const DIRECTORY_SOCIAL_NETWORKS = ["tiktok", "instagram", "facebook"] as const;
export type DirectorySocialNetwork = (typeof DIRECTORY_SOCIAL_NETWORKS)[number];
export type DirectorySocialFeature = `social.${DirectorySocialNetwork}`;
export const DIRECTORY_PROFILE_FEATURES = [
  "profile.configured",
  "profile.hours",
  "profile.offerings",
] as const;
export type DirectoryProfileFeature = (typeof DIRECTORY_PROFILE_FEATURES)[number];
export const DIRECTORY_EDITABLE_FEATURES = [
  "edit.name",
  "edit.description",
  "edit.category",
  "edit.keywords",
  "edit.contact",
  "edit.location",
  "edit.images",
  "edit.profile_content",
] as const;
export type DirectoryEditFeature = (typeof DIRECTORY_EDITABLE_FEATURES)[number] | DirectorySocialFeature;

export const DIRECTORY_SOCIAL_LABELS: Record<DirectorySocialNetwork, string> = {
  tiktok: "TikTok",
  instagram: "Instagram",
  facebook: "Facebook",
};

export function socialFeature(network: DirectorySocialNetwork): DirectorySocialFeature {
  return `social.${network}`;
}

/** Canonicaliza el catálogo de prestaciones para persistir solo permisos conocidos. */
export function normalizeDirectorySocialFeatures(features: readonly string[] = []): DirectorySocialFeature[] {
  const allowed = DIRECTORY_SOCIAL_NETWORKS.map(socialFeature);
  return [...new Set(features)].filter((feature): feature is DirectorySocialFeature =>
    allowed.includes(feature as DirectorySocialFeature),
  );
}

/** Normalizes profile modules and social networks granted by a new listing invitation. */
export function normalizeDirectoryInvitationFeatures(features: readonly string[] = []): string[] {
  const allowed = [...DIRECTORY_PROFILE_FEATURES, ...DIRECTORY_SOCIAL_NETWORKS.map(socialFeature)];
  return [...new Set(features)].filter((feature) => allowed.includes(feature as (typeof allowed)[number]));
}

/** Filtra los campos disponibles para un enlace de edición según el origen de la ficha. */
export function normalizeDirectoryEditFeatures(
  features: readonly string[] = [],
  origin: "manual" | "saas" = "manual",
): DirectoryEditFeature[] {
  const editable = origin === "manual" ? DIRECTORY_EDITABLE_FEATURES : [];
  const allowed = [...editable, ...DIRECTORY_SOCIAL_NETWORKS.map(socialFeature)];
  return [...new Set(features)].filter((feature): feature is DirectoryEditFeature =>
    allowed.includes(feature as DirectoryEditFeature),
  );
}

/** Acepta solo URLs de perfil de la red indicada, nunca dominios parecidos. */
export function normalizeSocialProfileUrl(network: DirectorySocialNetwork, raw: string): string | null {
  const value = raw.trim();
  if (!value || value.length > 300 || /[<>\s]/.test(value)) return null;
  try {
    // Muchas personas copian el dominio y perfil sin protocolo desde una app.
    // Completarlo aquí mantiene la validación estricta y evita rechazar esos enlaces.
    const url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
    const host = url.hostname.toLowerCase();
    const allowedHosts: Record<DirectorySocialNetwork, string[]> = {
      tiktok: ["tiktok.com"],
      instagram: ["instagram.com"],
      facebook: ["facebook.com", "fb.com"],
    };
    if (
      !(url.protocol === "https:" || url.protocol === "http:") ||
      url.username ||
      url.password ||
      url.port
    ) {
      return null;
    }
    if (!allowedHosts[network].some((base) => host === base || host.endsWith(`.${base}`))) return null;
    const segments = url.pathname.split("/").filter(Boolean);
    if (!segments.length || segments[0] === "watch") return null;
    if (segments[0] === "share" && !(network === "facebook" && segments.length === 2)) return null;
    if (network === "tiktok" && !segments[0]!.startsWith("@")) return null;
    if (network === "instagram" && ["p", "reel", "stories", "explore"].includes(segments[0]!)) return null;
    return url.toString();
  } catch {
    return null;
  }
}

export function sanitizeDirectorySocialLinks(input: unknown): Record<string, string> | null {
  if (input == null) return {};
  if (typeof input !== "object" || Array.isArray(input)) return null;
  const result: Record<string, string> = {};
  for (const [rawKey, rawValue] of Object.entries(input)) {
    const key = rawKey.toLowerCase() as DirectorySocialNetwork;
    if (!(DIRECTORY_SOCIAL_NETWORKS as readonly string[]).includes(key)) return null;
    if (typeof rawValue !== "string" || !rawValue.trim()) continue;
    const normalized = normalizeSocialProfileUrl(key, rawValue);
    if (!normalized) return null;
    result[key] = normalized;
  }
  return result;
}

/** Genera un token de invitacion criptograficamente seguro (32 bytes, base64url). */
export function generateInvitationToken(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** sha256 hex. Debe coincidir con `encode(digest(token,'sha256'),'hex')` en SQL. */
export async function hashInvitationToken(token: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(token));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

/** Fragmento del token apto para mostrar en el admin (nunca el token completo). */
export function invitationTokenPrefix(token: string, length = 8): string {
  return token.slice(0, length);
}

/** Calcula el vencimiento segun la opcion elegida. */
export function computeInvitationExpiry(
  option: InvitationExpiryOption,
  customIso?: string,
  now: Date = new Date(),
): string {
  if (option === "24h") return new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString();
  if (option === "7d") return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();
  if (!customIso) throw new Error("Selecciona una fecha de vencimiento.");
  const date = new Date(customIso);
  if (Number.isNaN(date.getTime())) throw new Error("La fecha de vencimiento no es valida.");
  if (date.getTime() <= now.getTime()) throw new Error("La fecha de vencimiento debe ser futura.");
  return date.toISOString();
}

/** Estado efectivo del enlace, considerando vencimiento y revocacion. */
export function effectiveInvitationStatus(
  invitation: { status: DirectoryInvitationStatus; expires_at: string; revoked_at: string | null },
  now: Date = new Date(),
): DirectoryInvitationStatus {
  if (invitation.revoked_at || invitation.status === "revoked") return "revoked";
  if (new Date(invitation.expires_at).getTime() <= now.getTime()) return "expired";
  return invitation.status;
}

export function isInvitationUsable(
  invitation: { status: DirectoryInvitationStatus; expires_at: string; revoked_at: string | null },
  now: Date = new Date(),
): boolean {
  const status = effectiveInvitationStatus(invitation, now);
  return status === "pending" || status === "submitted";
}

/** Enlace publico que se comparte con el propietario. */
export function buildInvitationUrl(baseUrl: string, token: string): string {
  return `${baseUrl.replace(/\/+$/, "")}/invitacion/${token}`;
}

export type DirectorySubmissionField =
  | "name"
  | "category"
  | "category_id"
  | "keywords"
  | "description"
  | "department"
  | "province"
  | "district"
  | "address"
  | "phone"
  | "whatsapp"
  | "website_url"
  | "social_links"
  | "consent_accepted";

export interface DirectoryValidationResult {
  valid: boolean;
  errors: Partial<Record<DirectorySubmissionField, string>>;
  sanitized: DirectorySubmissionInput;
}

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

const PHONE_PATTERN = /^[+()\-\s\d]{6,40}$/;

function clean(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/**
 * Valida y normaliza los datos del formulario de negocio. Se usa en cliente
 * (feedback inmediato) y su resultado normalizado es el que viaja al servidor.
 */
export function validateDirectorySubmission(input: DirectorySubmissionInput): DirectoryValidationResult {
  const errors: DirectoryValidationResult["errors"] = {};

  const name = clean(input.name) ?? "";
  if (name.length < 2) errors.name = "El nombre comercial es obligatorio.";
  else if (name.length > 200) errors.name = "El nombre no debe superar 200 caracteres.";

  const category = clean(input.category);
  if (category && category.length > 120) errors.category = "Maximo 120 caracteres.";

  const categoryId = clean(input.category_id);
  if (categoryId && !isDirectoryCategoryId(categoryId)) {
    errors.category_id = "Elige una categoria del listado.";
  }

  if (!isValidDirectoryKeywords(input.keywords)) {
    errors.keywords = "Se permiten hasta 3 palabras clave de 60 caracteres como maximo.";
  }
  const keywords = sanitizeDirectoryKeywords(input.keywords);

  const description = clean(input.description);
  if (description && description.length > 2000) errors.description = "Maximo 2000 caracteres.";

  const department = clean(input.department);
  if (department && department.length > 120) errors.department = "Maximo 120 caracteres.";

  const province = clean(input.province);
  if (province && province.length > 120) errors.province = "Maximo 120 caracteres.";

  const district = clean(input.district);
  if (district && district.length > 120) errors.district = "Maximo 120 caracteres.";

  const address = clean(input.address);
  if (address && address.length > 300) errors.address = "Maximo 300 caracteres.";

  const phone = clean(input.phone);
  if (phone && !PHONE_PATTERN.test(phone)) errors.phone = "Telefono invalido.";

  const whatsapp = clean(input.whatsapp);
  if (whatsapp && !PHONE_PATTERN.test(whatsapp)) errors.whatsapp = "WhatsApp invalido.";

  const websiteUrl = clean(input.website_url);
  if (websiteUrl && !isHttpUrl(websiteUrl)) {
    errors.website_url = "La pagina web debe empezar por http:// o https://.";
  }

  const socials = sanitizeDirectorySocialLinks(input.social_links);
  if (!socials) errors.social_links = "Usa enlaces de perfil válidos de TikTok, Instagram o Facebook.";

  if (input.consent_accepted !== true) {
    errors.consent_accepted = "Debes aceptar el aviso de privacidad.";
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
    sanitized: {
      name,
      category,
      category_id: categoryId,
      keywords,
      description,
      department,
      province,
      district,
      address,
      phone,
      whatsapp,
      website_url: websiteUrl,
      social_links: socials ?? {},
      consent_accepted: input.consent_accepted === true,
    },
  };
}
